import { useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichEditor } from "@/components/studio/RichEditor";
import { streamSSE } from "@/lib/api";

const CHIPS = ["Make it warmer and more personal", "Shorten by a third", "Simplify for teenage readers", "Add a reflection exercise with writing lines", "Add a LOOK callout and a pull quote"];

const Rewriter = ({ productId, section, ensureSaved, onAccept }) => {
  const [instruction, setInstruction] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const ctrl = useRef(null);

  const run = async () => {
    if (!instruction.trim()) return toast.error("Tell the AI what to change");
    setBusy(true);
    setOut("");
    try {
      await ensureSaved();
      ctrl.current = new AbortController();
      await streamSSE(`/products/${productId}/sections/${section.id}/ai/rewrite`, {
        body: { instruction, body: section.body, heading: section.heading },
        signal: ctrl.current.signal,
        onEvent: (ev) => {
          if (ev.type === "delta") setOut((o) => o + ev.text);
          if (ev.type === "error") toast.error(ev.message);
        },
      });
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const clean = out.replace(/^```[a-z]*\s*|\s*```$/g, "").trim();

  return (
    <div className="mt-4 border border-[#5E2B38]/25 bg-[#FAF7F0] p-4 space-y-3" data-testid={`rewrite-panel-${section.id}`}>
      <div className="eyebrow text-[#5E2B38]">Rewrite this section with AI</div>
      <div className="flex flex-wrap gap-1.5">
        {CHIPS.map((c) => (
          <button key={c} onClick={() => setInstruction(c)} className="text-xs px-2.5 py-1 border border-[#2A2325]/15 hover:border-[#5E2B38] hover:text-[#5E2B38] transition-colors bg-white">{c}</button>
        ))}
      </div>
      <Textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={2} placeholder="e.g. Rewrite for a 14-year-old reader, keep the facts" className="bg-white" data-testid={`rewrite-instruction-${section.id}`} />
      <div className="flex gap-2">
        <Button size="sm" onClick={run} disabled={busy} className="rounded-none gap-1.5" data-testid={`rewrite-run-${section.id}`}><Sparkles size={14} />{busy ? "Rewriting…" : "Rewrite"}</Button>
        {busy && <Button size="sm" variant="outline" className="rounded-none" onClick={() => ctrl.current?.abort()}>Stop</Button>}
      </div>
      {out && (
        <div className="bg-white border border-[#2A2325]/10">
          <div className="rich-editor p-4 max-h-80 overflow-y-auto" dangerouslySetInnerHTML={{ __html: clean }} data-testid={`rewrite-output-${section.id}`} />
          {!busy && (
            <div className="flex gap-2 p-3 border-t border-[#2A2325]/10">
              <Button size="sm" className="rounded-none" onClick={() => { onAccept(clean); setOut(""); }} data-testid={`rewrite-accept-${section.id}`}>Use this version</Button>
              <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setOut("")} data-testid={`rewrite-discard-${section.id}`}>Discard</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SectionCard = ({ index, total, section, productId, onChange, onMove, onDelete, ensureSaved, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen || index === 0);
  const [rewrite, setRewrite] = useState(false);
  return (
    <div className="bg-[#FFFDF9] border border-[#2A2325]/10" data-testid={`section-card-${index}`}>
      <div className="flex items-center gap-3 p-4">
        <span className="font-display italic text-xl text-[#5E2B38] w-10 shrink-0">{["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII","XIII","XIV","XV","XVI","XVII","XVIII","XIX","XX"][index] || index + 1}</span>
        <Input value={section.heading} onChange={(e) => onChange({ ...section, heading: e.target.value })} className="font-display text-xl md:text-xl border-0 shadow-none focus-visible:ring-0 px-0 bg-transparent" data-testid={`section-heading-input-${index}`} />
        <div className="flex items-center gap-1 text-[#5C5255] shrink-0">
          <button disabled={index === 0} onClick={() => onMove(-1)} className="p-1.5 hover:text-[#5E2B38] disabled:opacity-30" aria-label="Move up" data-testid={`section-up-${index}`}><ArrowUp size={15} /></button>
          <button disabled={index === total - 1} onClick={() => onMove(1)} className="p-1.5 hover:text-[#5E2B38] disabled:opacity-30" aria-label="Move down" data-testid={`section-down-${index}`}><ArrowDown size={15} /></button>
          <button onClick={onDelete} className="p-1.5 hover:text-[#9B2C2C]" aria-label="Delete section" data-testid={`section-delete-${index}`}><Trash2 size={15} /></button>
          <button onClick={() => setOpen(!open)} className="p-1.5 hover:text-[#5E2B38]" aria-label="Toggle" data-testid={`section-toggle-${index}`}><ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4">
          <RichEditor value={section.body} onChange={(body) => onChange({ ...section, body })} testId={`section-editor-${index}`} />
          <button onClick={() => setRewrite(!rewrite)} className="mt-3 text-sm text-[#5E2B38] flex items-center gap-1.5 hover:underline" data-testid={`section-rewrite-toggle-${index}`}>
            <Sparkles size={14} /> {rewrite ? "Close AI rewrite" : "Rewrite or polish with AI"}
          </button>
          {rewrite && <Rewriter productId={productId} section={section} ensureSaved={ensureSaved} onAccept={(body) => onChange({ ...section, body })} />}
        </div>
      )}
    </div>
  );
};
